import type { Product } from '../types/catalog'

/**
 * Voorraadregels van de winkel — één plek, zodat winkelmand, productkaart,
 * productpagina en structured data (shop/seo/schemas.ts) hetzelfde zeggen.
 * `stock` onbekend (undefined) = leverbaar; met een server komt het uit `GET /catalog`.
 * De server hoort exact dezelfde regels af te dwingen (zie tests/vectors/stock.json).
 */

/** Meer dan dit per product in één bestelling is vrijwel altijd een vergissing (of doorverkoop). */
export const MAX_PER_LINE = 10

/** Onder of gelijk aan dit aantal tonen we "Nog X op voorraad". */
export const LOW_STOCK_AT = 5

/**
 * De voorraad zoals de regels hem lezen: een heel getal ≥ 0, of `undefined` (= niet bijgehouden).
 *
 * Een server of import kan een negatieve voorraad teruggeven (backorders, handmatige correctie) en
 * kapotte data kan NaN of een kommagetal opleveren. Zonder deze stap was een product met
 * voorraad -2 "niet uitverkocht" maar ook niet toe te voegen, en gaf NaN een maximum van NaN.
 * Twijfel = niet verkopen: NaN telt als 0. Alleen `Infinity` betekent "onbeperkt".
 */
export function stockOf(p: Product): number | undefined {
  const s = p.stock
  // `null` kan uit JSON komen, ook al staat het niet in het type.
  if (s === undefined || (s as unknown) === null) return undefined
  if (typeof s !== 'number' || Number.isNaN(s)) return 0
  if (s === Infinity) return undefined
  return Math.max(0, Math.floor(s))
}

export const isSoldOut = (p: Product) => stockOf(p) === 0

/** Hoeveel producten uit een set (protocol, levering) uitverkocht zijn. */
export const countSoldOut = (items: readonly Product[]) => items.filter(isSoldOut).length

/** Alles uit een niet-lege set is uitverkocht: dan valt er niets toe te voegen. */
export const isAllSoldOut = (items: readonly Product[]) => items.length > 0 && items.every(isSoldOut)

export const isLowStock = (p: Product) => {
  const s = stockOf(p)
  return s !== undefined && s > 0 && s <= LOW_STOCK_AT
}

/** Hoeveel er maximaal in de winkelmand mag. */
export const maxQty = (p: Product) => {
  // Eén kuur per bestelling: hij komt op één klant en één behandelplan.
  if (p.kind === 'kuur') return 1
  const s = stockOf(p)
  return s === undefined ? MAX_PER_LINE : Math.min(s, MAX_PER_LINE)
}

/** Cadeaubonnen en kuren gaan niet op de post: geen verzending, tellen niet mee voor de cadeaustaffel. */
export const isPhysical = (p: Product) => p.kind !== 'giftcard' && p.kind !== 'kuur'

/**
 * Een aantal uit invoer of opslag: altijd een geheel getal tussen 0 en het maximum.
 * Alleen getallen en getal-tekst tellen; `true`, `[5]` of `null` worden 0 (niet 1 of 5,
 * wat `Number()` ervan zou maken).
 */
export const clampQty = (p: Product, qty: unknown) => {
  const raw = typeof qty === 'number' ? qty : typeof qty === 'string' && qty.trim() !== '' ? Number(qty) : NaN
  const n = Math.floor(raw)
  return Number.isFinite(n) ? Math.max(0, Math.min(n, maxQty(p))) : 0
}
